import { Box } from '@mui/material';
import {
  GridColDef,
  GridRenderCellParams,
  GridTreeNodeWithRender,
} from '@mui/x-data-grid';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { RootState } from '../../store/store';
import CustomChip from '../components/CustomChip';
import { EditIcon, TrashIcon } from '../components/Icons';
import { ERole } from '../enums/user/ERole';
import { ETheme } from '../enums/user/ETheme';
import { formatDate } from '../functions/formatDate';
import { getHolidayTypeColor } from '../functions/getChipColors';
import { IHoliday } from '../interfaces/holiday/IHoliday';
import { darkTheme, lightTheme } from '../mui/theme';

export const useHolidayColumns = (
  setSelectedHoliday: (project: IHoliday) => void,
  setShowDeleteModal: (show: boolean) => void,
): GridColDef[] => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { userData } = useSelector((state: RootState) => state.auth);
  const isNormalUser = userData?.role === ERole.USER;
  return [
    { field: 'id', headerName: t('general.id'), width: 50 },
    {
      field: 'name',
      headerName: t('general.name'),
      flex: 1,
      renderCell: (
        params: GridRenderCellParams<any, any, any, GridTreeNodeWithRender>,
      ) => (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          {params.value}
        </div>
      ),
    },
    {
      field: 'startDate',
      headerName: t('general.dates'),
      flex: 1,
      renderCell: (
        params: GridRenderCellParams<any, any, any, GridTreeNodeWithRender>,
      ) => {
        const { startDate, endDate } = params?.row;
        return (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            {formatDate(startDate)} - {formatDate(endDate)}
          </div>
        );
      },
    },
    {
      field: 'country',
      headerName: t('general.country'),
      width: 200,
      renderCell: (
        params: GridRenderCellParams<any, any, any, GridTreeNodeWithRender>,
      ) => (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          {t(`country.${params.value}`)}
        </div>
      ),
    },
    {
      field: 'holidayType',
      headerName: t('general.type'),
      width: 200,
      renderCell: (
        params: GridRenderCellParams<any, any, any, GridTreeNodeWithRender>,
      ) => (
        <CustomChip
          label={t(`holidays.type.${params.value}`)} // Translated status
          color={getHolidayTypeColor(params.value)}
        />
      ),
    },
    {
      field: 'actions',
      headerName: t('general.action'),
      width: 150,
      headerClassName: userData?.role === ERole.USER ? 'hideColumn' : '',
      sortable: false,
      renderCell: (
        params: GridRenderCellParams<any, any, any, GridTreeNodeWithRender>,
      ) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          {/* Edit or View Icon */}
          {!isNormalUser && (
            <>
              <span
                style={{ cursor: 'pointer', display: 'flex' }}
                onClick={() => navigate(`/admin/holiday/${params.row?.id}`)}
              >
                <EditIcon
                  fill={
                    userData?.theme === ETheme.LIGHT
                      ? lightTheme.palette.text.primary
                      : darkTheme.palette.text.primary
                  }
                />
              </span>

              {/* Delete Icon */}
              <span
                style={{ cursor: 'pointer' }}
                className={'d-flex ms-3'}
                onClick={() => {
                  setSelectedHoliday(params.row);
                  setShowDeleteModal(true);
                }}
              >
                <TrashIcon
                  fill={
                    userData?.theme === ETheme.LIGHT
                      ? lightTheme.palette.text.primary
                      : darkTheme.palette.text.primary
                  }
                />
              </span>
            </>
          )}
        </Box>
      ),
    },
  ];
};
